import React, { useState, useEffect } from 'react';
import {
 Box,
 Card,
 CardContent,
 Typography,
 Button,
 Chip,
 List,
 ListItem,
 ListItemText,
 ListItemSecondaryAction,
 IconButton,
 Dialog,
 DialogTitle,
 DialogContent,
 DialogActions,
 TextField,
 Grid,
 Avatar,
 Badge,
 Divider,
 Paper,
 Tooltip
} from '@mui/material';
import {
 Person,
 Assignment,
 Chat,
 CheckCircle,
 Pending,
 PlayArrow,
 Stop,
 Refresh,
 Message,
 Group
} from '@mui/icons-material';
import './IndividualRoleViewer.css';

const IndividualRoleViewer = () => {
 const [selectedRole, setSelectedRole] = useState(null);
 const [individualRoles, setIndividualRoles] = useState([]);
 const [chatOpen, setChatOpen] = useState(false);
 const [currentTask, setCurrentTask] = useState(null);
 const [taskStatus, setTaskStatus] = useState({});
 const [chatMessages, setChatMessages] = useState([]);
 const [newMessage, setNewMessage] = useState('');
 const [activeUsers, setActiveUsers] = useState(new Set());

 // Mock data for individual roles extracted from team roles
 const mockIndividualRoles = [
 {
 id: 'person1',
 name: 'Alex Johnson',
 role: 'Frontend Developer',
 avatar: 'A',
 color: '#4CAF50',
 tasks: [
 {
 id: 'task1',
 title: 'Analyze authentication requirements and design',
 description: 'Gather and analyze requirements for the authentication system, design the authentication flow (e.g., JWT or OAuth), and create technical specifications.',
 phase: 1,
 status: 'completed',
 priority: 'high'
 },
 {
 id: 'task2',
 title: 'Analyze database requirements and design schema',
 description: 'Analyze requirements for blog post storage and design a normalized SQL or NoSQL schema, including tables/collections for posts and metadata.',
 phase: 2,
 status: 'in_progress',
 priority: 'medium'
 },
 {
 id: 'task3',
 title: 'Analyze UI requirements and design',
 description: 'Gather requirements for the blog post creation UI and design wireframes or mockups for the interface, focusing on usability.',
 phase: 3,
 status: 'pending',
 priority: 'low'
 }
 ],
 skills: ['React', 'JavaScript', 'UI/UX Design', 'Figma'],
 experience: '3-5 years'
 },
 {
 id: 'person2',
 name: 'Sarah Chen',
 role: 'Backend Developer',
 avatar: 'S',
 color: '#2196F3',
 tasks: [
 {
 id: 'task4',
 title: 'Develop backend authentication APIs',
 description: 'Implement backend APIs for user registration, login, and token management, including database integration for storing user credentials securely.',
 phase: 1,
 status: 'completed',
 priority: 'high'
 },
 {
 id: 'task5',
 title: 'Implement database schema and queries',
 description: 'Create the database schema in the chosen database system (e.g., PostgreSQL or MongoDB) and develop queries for CRUD operations.',
 phase: 2,
 status: 'in_progress',
 priority: 'high'
 },
 {
 id: 'task6',
 title: 'Develop backend APIs for post creation',
 description: 'Implement backend APIs to handle blog post creation and editing, integrating with the database schema for storage.',
 phase: 3,
 status: 'pending',
 priority: 'medium'
 }
 ],
 skills: ['Node.js', 'Express', 'PostgreSQL', 'MongoDB', 'JWT'],
 experience: '4-6 years'
 },
 {
 id: 'person3',
 name: 'Mike Rodriguez',
 role: 'UI/UX Developer',
 avatar: 'M',
 color: '#FF9800',
 tasks: [
 {
 id: 'task7',
 title: 'Create frontend authentication UI',
 description: 'Develop React-based UI components for user registration, login, and password management, ensuring a responsive and user-friendly interface.',
 phase: 1,
 status: 'completed',
 priority: 'medium'
 },
 {
 id: 'task8',
 title: 'Develop schema management interface',
 description: 'Create a basic admin interface for viewing and managing the blog post schema, ensuring accessibility for future updates.',
 phase: 2,
 status: 'pending',
 priority: 'low'
 },
 {
 id: 'task9',
 title: 'Build frontend post creation UI',
 description: 'Develop React components for the blog post creation and editing interface, supporting text input and basic formatting.',
 phase: 3,
 status: 'pending',
 priority: 'medium'
 }
 ],
 skills: ['React', 'Material-UI', 'CSS', 'Responsive Design'],
 experience: '2-4 years'
 },
 {
 id: 'person4',
 name: 'Emily Watson',
 role: 'QA/Testing Engineer',
 avatar: 'E',
 color: '#9C27B0',
 tasks: [
 {
 id: 'task10',
 title: 'Test and integrate authentication',
 description: 'Write automated tests (unit and integration) for the authentication system and ensure seamless integration between frontend and backend components.',
 phase: 1,
 status: 'in_progress',
 priority: 'high'
 },
 {
 id: 'task11',
 title: 'Test schema and document',
 description: 'Write automated tests for the database schema and queries, and produce documentation detailing the schema structure and usage.',
 phase: 2,
 status: 'pending',
 priority: 'medium'
 },
 {
 id: 'task12',
 title: 'Test UI and integration',
 description: 'Write automated tests for the post creation UI and APIs, ensuring seamless integration and functionality across components.',
 phase: 3,
 status: 'pending',
 priority: 'high'
 }
 ],
 skills: ['Jest', 'Cypress', 'Manual Testing', 'API Testing'],
 experience: '3-5 years'
 }
 ];

 // Mock chat messages
 const mockChatMessages = [
 {
 id: 1,
 senderId: 'person1',
 senderName: 'Alex Johnson',
 message: 'Hey team! I\'ve completed the authentication design. The wireframes are ready for review.',
 timestamp: new Date(Date.now() - 1000 * 60 * 30), // 30 minutes ago
 type: 'text'
 },
 {
 id: 2,
 senderId: 'person2',
 senderName: 'Sarah Chen',
 message: 'Great work Alex! I\'m working on the backend APIs now. Should be ready by EOD.',
 timestamp: new Date(Date.now() - 1000 * 60 * 25), // 25 minutes ago
 type: 'text'
 },
 {
 id: 3,
 senderId: 'person4',
 senderName: 'Emily Watson',
 message: 'I can start testing the auth flow once Sarah completes the APIs. What\'s the timeline?',
 timestamp: new Date(Date.now() - 1000 * 60 * 20), // 20 minutes ago
 type: 'text'
 },
 {
 id: 4,
 senderId: 'person3',
 senderName: 'Mike Rodriguez',
 message: 'I\'ll need the API endpoints to start building the frontend components. Can you share the API docs?',
 timestamp: new Date(Date.now() - 1000 * 60 * 15), // 15 minutes ago
 type: 'text'
 },
 {
 id: 5,
 senderId: 'person2',
 senderName: 'Sarah Chen',
 message: 'Sure! I\'ll create the API documentation and share it in the next hour.',
 timestamp: new Date(Date.now() - 1000 * 60 * 10), // 10 minutes ago
 type: 'text'
 }
 ];

 useEffect(() => {
 setIndividualRoles(mockIndividualRoles);
 setChatMessages(mockChatMessages);
 setActiveUsers(new Set(['person1', 'person2', 'person3', 'person4']));
 }, []);

 const getStatusColor = (status) => {
 switch (status) {
 case 'completed': return '#4CAF50';
 case 'in_progress': return '#FF9800';
 case 'pending': return '#9E9E9E';
 default: return '#9E9E9E';
 }
 };

 const getStatusIcon = (status) => {
 switch (status) {
 case 'completed': return <CheckCircle />;
 case 'in_progress': return <PlayArrow />;
 case 'pending': return <Pending />;
 default: return <Pending />;
 }
 };

 const getPriorityColor = (priority) => {
 switch (priority) {
 case 'high': return '#F44336';
 case 'medium': return '#FF9800';
 case 'low': return '#4CAF50';
 default: return '#9E9E9E';
 }
 };

 const handleTaskStatusChange = (taskId, newStatus) => {
 setTaskStatus(prev => ({
 ...prev,
 [taskId]: newStatus
 }));
 };

 const handleSendMessage = () => {
 if (newMessage.trim()) {
 const message = {
 id: chatMessages.length + 1,
 senderId: 'person1', // Current user - in real app, this would be dynamic
 senderName: 'Alex Johnson',
 message: newMessage.trim(),
 timestamp: new Date(),
 type: 'text'
 };
 setChatMessages(prev => [...prev, message]);
 setNewMessage('');
 }
 };

 const formatTime = (timestamp) => {
 return timestamp.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
 };

 const getOnlineStatus = (userId) => {
 return activeUsers.has(userId);
 };

 return (
 <Box sx={{ p: 3, backgroundColor: '#f5f5f5', minHeight: '100vh' }}>
 <Typography variant="h4" gutterBottom sx={{ color: '#333', fontWeight: 'bold' }}>
 Individual Role Dashboard
 </Typography>
 
 <Grid container spacing={3}>
 {/* Role Cards */}
 {individualRoles.map((role) => (
 <Grid item xs={12} md={6} lg={3} key={role.id}>
 <Card 
 sx={{ 
 height: '100%',
 cursor: 'pointer',
 transition: 'transform 0.2s',
 '&:hover': {
 transform: 'translateY(-4px)',
 boxShadow: 4
 }
 }}
 onClick={() => setSelectedRole(role)}
 >
 <CardContent>
 <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
 <Avatar 
 sx={{ 
 bgcolor: role.color, 
 mr: 2,
 width: 48,
 height: 48
 }}
 >
 {role.avatar}
 </Avatar>
 <Box>
 <Typography variant="h6" component="div">
 {role.name}
 </Typography>
 <Typography variant="body2" color="text.secondary">
 {role.role}
 </Typography>
 </Box>
 </Box>
 
 <Box sx={{ mb: 2 }}>
 <Typography variant="body2" color="text.secondary" gutterBottom>
 Experience: {role.experience}
 </Typography>
 <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
 {role.skills.slice(0, 3).map((skill, index) => (
 <Chip 
 key={index} 
 label={skill} 
 size="small" 
 variant="outlined"
 sx={{ fontSize: '0.7rem' }}
 />
 ))}
 {role.skills.length > 3 && (
 <Chip 
 label={`+${role.skills.length - 3}`} 
 size="small" 
 variant="outlined"
 sx={{ fontSize: '0.7rem' }}
 />
 )}
 </Box>
 </Box>

 <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
 <Typography variant="body2" color="text.secondary">
 Tasks: {role.tasks.filter(t => t.status === 'completed').length}/{role.tasks.length}
 </Typography>
 <Badge 
 color={getOnlineStatus(role.id) ?"success" :"default"} 
 variant="dot"
 >
 <Button
 size="small"
 startIcon={<Chat />}
 onClick={(e) => {
 e.stopPropagation();
 setChatOpen(true);
 }}
 >
 Chat
 </Button>
 </Badge>
 </Box>
 </CardContent>
 </Card>
 </Grid>
 ))}
 </Grid>

 {/* Task Detail Dialog */}
 <Dialog 
 open={selectedRole !== null} 
 onClose={() => setSelectedRole(null)}
 maxWidth="md"
 fullWidth
 >
 <DialogTitle>
 <Box sx={{ display: 'flex', alignItems: 'center' }}>
 <Avatar sx={{ bgcolor: selectedRole?.color, mr: 2 }}>
 {selectedRole?.avatar}
 </Avatar>
 <Box>
 <Typography variant="h6">{selectedRole?.name}</Typography>
 <Typography variant="body2" color="text.secondary">
 {selectedRole?.role}
 </Typography>
 </Box>
 </Box>
 </DialogTitle>
 
 <DialogContent>
 {selectedRole && (
 <>
 <Box sx={{ mb: 3 }}>
 <Typography variant="h6" gutterBottom>
 Skills & Experience
 </Typography>
 <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mb: 2 }}>
 {selectedRole.skills.map((skill, index) => (
 <Chip key={index} label={skill} color="primary" variant="outlined" />
 ))}
 </Box>
 <Typography variant="body2" color="text.secondary">
 Experience: {selectedRole.experience}
 </Typography>
 </Box>

 <Divider sx={{ my: 2 }} />

 <Typography variant="h6" gutterBottom>
 Assigned Tasks
 </Typography>
 
 <List>
 {selectedRole.tasks.map((task) => (
 <ListItem key={task.id} sx={{ border: 1, borderColor: 'divider', borderRadius: 1, mb: 1 }}>
 <ListItemText
 primary={
 <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
 <Typography variant="subtitle1">{task.title}</Typography>
 <Chip 
 label={task.priority} 
 size="small" 
 sx={{ 
 bgcolor: getPriorityColor(task.priority),
 color: 'white',
 fontSize: '0.7rem'
 }}
 />
 </Box>
 }
 secondary={
 <Box>
 <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
 Phase {task.phase}
 </Typography>
 <Typography variant="body2">
 {task.description}
 </Typography>
 </Box>
 }
 />
 <ListItemSecondaryAction>
 <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
 <Chip
 icon={getStatusIcon(task.status)}
 label={task.status.replace('_', ' ')}
 size="small"
 sx={{ 
 bgcolor: getStatusColor(task.status),
 color: 'white',
 fontSize: '0.7rem'
 }}
 />
 <Tooltip title="Update Status">
 <IconButton 
 size="small"
 onClick={() => setCurrentTask(task)}
 >
 <Refresh />
 </IconButton>
 </Tooltip>
 </Box>
 </ListItemSecondaryAction>
 </ListItem>
 ))}
 </List>
 </>
 )}
 </DialogContent>
 
 <DialogActions>
 <Button onClick={() => setSelectedRole(null)}>Close</Button>
 <Button 
 variant="contained" 
 startIcon={<Message />}
 onClick={() => {
 setChatOpen(true);
 setSelectedRole(null);
 }}
 >
 Open Team Chat
 </Button>
 </DialogActions>
 </Dialog>

 {/* Chat Dialog */}
 <Dialog 
 open={chatOpen} 
 onClose={() => setChatOpen(false)}
 maxWidth="md"
 fullWidth
 >
 <DialogTitle>
 <Box sx={{ display: 'flex', alignItems: 'center' }}>
 <Group sx={{ mr: 1 }} />
 Team Chat Room
 </Box>
 </DialogTitle>
 
 <DialogContent sx={{ height: '400px', p: 0 }}>
 <Paper sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
 {/* Chat Messages */}
 <Box sx={{ flex: 1, overflow: 'auto', p: 2 }}>
 {chatMessages.map((message) => {
 const sender = individualRoles.find(r => r.id === message.senderId);
 return (
 <Box key={message.id} sx={{ mb: 2, display: 'flex', alignItems: 'flex-start' }}>
 <Avatar 
 sx={{ 
 bgcolor: sender?.color || '#9E9E9E',
 width: 32,
 height: 32,
 mr: 1,
 mt: 0.5
 }}
 >
 {sender?.avatar || '?'}
 </Avatar>
 <Box sx={{ flex: 1 }}>
 <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
 <Typography variant="subtitle2" sx={{ fontWeight: 'bold' }}>
 {message.senderName}
 </Typography>
 <Typography variant="caption" color="text.secondary">
 {formatTime(message.timestamp)}
 </Typography>
 {getOnlineStatus(message.senderId) && (
 <Badge color="success" variant="dot" />
 )}
 </Box>
 <Typography variant="body2" sx={{ 
 bgcolor: 'grey.100', 
 p: 1, 
 borderRadius: 1,
 wordBreak: 'break-word'
 }}>
 {message.message}
 </Typography>
 </Box>
 </Box>
 );
 })}
 </Box>
 
 {/* Chat Input */}
 <Box sx={{ p: 2, borderTop: 1, borderColor: 'divider' }}>
 <Box sx={{ display: 'flex', gap: 1 }}>
 <TextField
 fullWidth
 placeholder="Type your message..."
 value={newMessage}
 onChange={(e) => setNewMessage(e.target.value)}
 onKeyPress={(e) => {
 if (e.key === 'Enter') {
 handleSendMessage();
 }
 }}
 size="small"
 />
 <Button 
 variant="contained" 
 onClick={handleSendMessage}
 disabled={!newMessage.trim()}
 >
 Send
 </Button>
 </Box>
 </Box>
 </Paper>
 </DialogContent>
 
 <DialogActions>
 <Button onClick={() => setChatOpen(false)}>Close</Button>
 </DialogActions>
 </Dialog>
 </Box>
 );
};

export default IndividualRoleViewer;




